import { useEffect } from 'react';

/**
 * Аппаратная кнопка «Назад» на Android — разбор нажатия по слоям.
 *
 * ЗАЧЕМ. До этого обработки не было вообще: нажатие на ЛЮБОМ экране закрывало
 * приложение. Жест «назад» от края срабатывает случайно чаще, чем кажется —
 * особенно когда палец идёт по краю длинного текста. Ученик, читавший задание
 * в мок-экзамене, терял попытку целиком.
 *
 * КАК УСТРОЕНО. Экран, которому есть что закрыть, кладёт свой обработчик в стек.
 * Нажатие достаётся ВЕРХНЕМУ — тому, что открылось последним. Вернул `true`,
 * значит нажатие поглощено и дальше не идёт; вернул `false` — пробуем следующий.
 * Так модальное окно закрывается раньше, чем происходит переход по истории, без
 * единого условия «а не открыто ли сейчас что-нибудь» в коде навигации.
 *
 * Стек, а не один обработчик: окно поверх окна — обычное дело (подтверждение
 * поверх формы), и закрываться они должны в обратном порядке.
 */

type BackHandler = () => boolean;

const stack: BackHandler[] = [];

/**
 * Кто следит, пуст ли стек. Свайп назад на iPhone (`lib/nativeNav.ts`) должен выключаться, пока в стеке кто-то
 * есть: системный жест, в отличие от кнопки Android, обработчиков не спрашивает и обошёл бы окно молча.
 */
const listeners = new Set<() => void>();

export function subscribeBackStack(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function backStackEmpty(): boolean {
  return stack.length === 0;
}

function notify(): void {
  for (const fn of listeners) fn();
}

export function pushBackHandler(handler: BackHandler): void {
  stack.push(handler);
  notify();
}

export function popBackHandler(handler: BackHandler): void {
  const i = stack.lastIndexOf(handler);
  if (i !== -1) stack.splice(i, 1);
  notify();
}

/**
 * Отдаёт нажатие обработчикам сверху вниз. `true` — кто-то его принял,
 * навигацией заниматься не нужно.
 */
export function runBackHandlers(): boolean {
  for (let i = stack.length - 1; i >= 0; i--) {
    try {
      if (stack[i]()) return true;
    } catch {
      // Сломавшийся обработчик не должен запирать кнопку — идём дальше вниз.
    }
  }
  return false;
}

/**
 * Регистрирует обработчик на время, пока `active`.
 *
 * `active` нужен потому, что модальные окна и вопросы живут в дереве и когда
 * закрыты: без флага закрытое окно продолжало бы перехватывать кнопку.
 */
export function useBackHandler(handler: BackHandler, active = true): void {
  useEffect(() => {
    if (!active) return;
    pushBackHandler(handler);
    return () => popBackHandler(handler);
  }, [handler, active]);
}
